<script>
  import AccountAvatarCard from './AccountAvatarCard.svelte';
  import AccountProfileCard from './AccountProfileCard.svelte';
  import AccountWorkCard from './AccountWorkCard.svelte';
  import AccountSecurityCard from './AccountSecurityCard.svelte';
  import ChangePasswordDialog from './ChangePasswordDialog.svelte';
  import ChangePinDialog from './ChangePinDialog.svelte';
  import SettingsState from '../shared/SettingsState.svelte';

  import ApiClient from '$lib/api';
  import { avatarContentUrl } from '$lib/avatar';
  import { notify } from '@components/toast';
  import { user as authStore } from '$lib/stores/store';

  const api = new ApiClient();

  $: user = $authStore;

  let form = {
    avatar: '',
    name: 'Khang Le',
    email: 'khang@example.com',
    provider: 'local',
    employeeCode: 'EMP001',
    department: 'IT Department',
    position: 'Senior Developer',
    phone: '0901234567',
    pinEnabled: true,
    pinLength: 4,
    maxAttempts: 5,
    lockSeconds: 60,
    bypassMilliseconds: 1000,
    lastLogin: '2026-06-03 08:30',
    status: 'Active'
  };

  let saving = false;
  let loadingAccount = false;
  let loadedAccountId = null;
  let loadError = '';
  let avatarPreview = '';
  let uploadingAvatar = false;
  let showChangePin = false;
  let showChangePassword = false;
  let changingPassword = false;

  $: if (user?.id && user.id !== loadedAccountId) {
    loadedAccountId = user.id;
    loadAccount(user.id);
  }

  function normalizeBypassMs(value) {
    const next = Number(value);

    if (!Number.isFinite(next)) return 1000;

    return Math.min(60000, Math.max(500, Math.round(next)));
  }

  async function loadAccount(accountId = user?.id) {
    if (!accountId) return;

    try {
      loadingAccount = true;
      loadError = '';
      avatarPreview = '';

      const account = await api.get(`/api/accounts/${accountId}`);
      const nextForm = { ...form, ...account };

      nextForm.hasPin = !!(nextForm.hasPin ?? nextForm.pinEnabled ?? nextForm.pinenabled);
      nextForm.bypassMilliseconds = normalizeBypassMs(nextForm.bypassMilliseconds);

      form = nextForm;
    } catch (err) {
      loadError = err?.message || 'Could not load account data';
      notify.error(loadError);
    } finally {
      loadingAccount = false;
    }
  }

  async function saveProfile() {
    const accountId = form.id || user?.id;

    if (!accountId) {
      notify.error('User information is missing');
      return;
    }

    try {
      saving = true;

      await api.put(`/api/accounts/${accountId}`, form);

      const security = await api.post('/api/update-security-bypass', {
        userId: accountId,
        bypassMilliseconds: normalizeBypassMs(form.bypassMilliseconds)
      });

      if (security?.success) {
        form.bypassMilliseconds = security.bypassMilliseconds;
      }

      notify.success('Profile updated');
    } catch (err) {
      notify.error(err?.message || 'Profile update failed');
    } finally {
      saving = false;
    }
  }

  function changePin() {
    showChangePin = true;
  }

  function changePassword() {
    showChangePassword = true;
  }

  async function savePin(event) {
    try {
      const { mode, currentPin, newPin, pinLength } = event.detail;
      const accountId = form.id || user?.id;

      if (!accountId) {
        notify.error('User information is missing');
        return;
      }

      const result =
        mode === 'create'
          ? await api.post('/api/create-pin', {
              userId: accountId,
              pin: newPin
            })
          : await api.post('/api/update-pin', {
              userId: accountId,
              oldPin: currentPin,
              newPin,
              pinLength
            });

      if (!result.success) {
        notify.error(result.message);
        return;
      }

      form.pinLength = pinLength;
      form.hasPin = true;
      form.pinEnabled = true;

      notify.success(mode === 'create' ? 'PIN created' : 'PIN updated');

      showChangePin = false;
    } catch (err) {
      notify.error(err?.message || 'PIN update failed');
    }
  }

  async function savePassword(event) {
    try {
      changingPassword = true;

      const { currentPassword, newPassword } = event.detail;
      const accountId = form.id || user?.id;

      if (!accountId) {
        notify.error('User information is missing');
        return;
      }

      const result = await api.post(`/api/accounts/${accountId}/change-password`, {
        currentPassword,
        newPassword
      });

      if (!result.success) {
        notify.error(result.message);
        return;
      }

      notify.success('Password changed');

      showChangePassword = false;
    } catch (err) {
      notify.error(err?.message || 'Password change failed');
    } finally {
      changingPassword = false;
    }
  }

  async function uploadAvatar(event) {
    const file = event.target.files?.[0];

    if (!file) return;

    const allowedTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
    if (!allowedTypes.has(file.type)) {
      notify.error('Avatar must be a JPEG, PNG, or WebP image');
      event.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      notify.error('Avatar must be 5 MB or smaller');
      event.target.value = '';
      return;
    }

    const previousAvatarPreview = avatarPreview;
    const localPreview = URL.createObjectURL(file);
    avatarPreview = localPreview;

    try {
      uploadingAvatar = true;
      const fd = new FormData();

      fd.append('file', file);

      const result = await api.post('/api/upload-avatar', fd);
      if (!result?.avatar || !result?.url) {
        throw new Error('Avatar upload returned an invalid response');
      }

      // POST /upload-avatar has already persisted the UUID v7 in users.avatar.
      form.avatar = result.avatar;
      avatarPreview = avatarContentUrl(result.avatar) || result.url;
      URL.revokeObjectURL(localPreview);
      authStore.update((current) => (
        current && String(current.id) === String(user?.id)
          ? { ...current, avatar: result.avatar }
          : current
      ));
      notify.success('Avatar updated');
    } catch (err) {
      avatarPreview = previousAvatarPreview;
      URL.revokeObjectURL(localPreview);
      notify.error(err?.message || 'Avatar upload failed');
    } finally {
      uploadingAvatar = false;
      event.target.value = '';
    }
  }
</script>

<div class="min-h-full bg-slate-100">
  <div class="mx-auto grid max-w-7xl gap-4 p-4 lg:grid-cols-[minmax(260px,360px)_minmax(0,1fr)] lg:p-6">
    {#if loadingAccount}
      <div class="lg:col-span-2">
        <SettingsState title="Loading account" message="Please wait while account settings are loaded." />
      </div>
    {:else if loadError}
      <div class="lg:col-span-2">
        <SettingsState type="error" title="Account unavailable" message={loadError} />
      </div>
    {:else}
      <div class="min-w-0">
        <AccountAvatarCard bind:form {avatarPreview} {uploadingAvatar} onUploadAvatar={uploadAvatar} />
      </div>

      <div class="min-w-0 space-y-4">
        <AccountProfileCard bind:form />
        <AccountWorkCard bind:form />
        <AccountSecurityCard bind:form onChangePin={changePin} onChangePassword={changePassword} />
      </div>
    {/if}
  </div>

  <div class="sticky bottom-0 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-sm backdrop-blur">
    <div class="mx-auto flex max-w-7xl justify-end">
      <button
        type="button"
        class="inline-flex h-10 items-center justify-center rounded-xl bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
        disabled={saving || loadingAccount || !!loadError}
        on:click={saveProfile}>
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  </div>
</div>

<ChangePinDialog
  {form}
  visible={showChangePin}
  pinLength={form.pinLength}
  hasPin={form.hasPin ?? form.pinEnabled ?? form.pinenabled}
  accountId={form.id || user?.id}
  on:close={() => (showChangePin = false)}
  on:save={savePin}
  on:deleted={() => {
    form.hasPin = false;
    form.pinEnabled = false;
    form.pinLength = null;
    form.bypassMilliseconds = normalizeBypassMs(form.bypassMilliseconds);
  }} />

<ChangePasswordDialog
  visible={showChangePassword}
  saving={changingPassword}
  accountId={form.id || user?.id}
  on:close={() => (showChangePassword = false)}
  on:save={savePassword} />
